import { a as getRenderingRef, f as forceUpdate, h, r as registerInstance, c as createEvent, H as Host } from './index-1755b6c5.js';
import { b as getAuthClient, c as authClientName, g as getEnvConfig, A as ApiClient, s as setEnv, a as getPublicApiClient, i as i18nProvider, l as loadTranslations } from './loadTranslations-27c194f9.js';
import { g as getSoftwares, a as getSoftwareTypes, S as SOFTWARE_TYPES, b as getSoftwareModulesByType, c as createRequestData } from './createCase-dbb0d561.js';

/**
 * This can be used like a Fragment in React. https://legacy.reactjs.org/docs/fragments.html
 */
const Fragment = (_props, children) => [...children];

const appendToMap = (map, propName, value) => {
    const items = map.get(propName);
    if (!items) {
        map.set(propName, [value]);
    }
    else if (!items.includes(value)) {
        items.push(value);
    }
};
const debounce = (fn, ms) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            timeoutId = 0;
            fn(...args);
        }, ms);
    };
};

/**
 * Check if a possible element isConnected.
 * The property might not be there, so we check for it.
 *
 * We want it to return true if isConnected is not a property,
 * otherwise we would remove these elements and would not update.
 *
 * Better leak in Edge than to be useless.
 */
const isConnected = (maybeElement) => !('isConnected' in maybeElement) || maybeElement.isConnected;
const cleanupElements = debounce((map) => {
    for (let key of map.keys()) {
        map.set(key, map.get(key).filter(isConnected));
    }
}, 2_000);
const stencilSubscription = () => {
    if (typeof getRenderingRef !== 'function') {
        // If we are not in a stencil project, we do nothing.
        // This function is not really exported by @stencil/core.
        return {};
    }
    const elmsToUpdate = new Map();
    return {
        dispose: () => elmsToUpdate.clear(),
        get: (propName) => {
            const elm = getRenderingRef();
            if (elm) {
                appendToMap(elmsToUpdate, propName, elm);
            }
        },
        set: (propName) => {
            const elements = elmsToUpdate.get(propName);
            if (elements) {
                elmsToUpdate.set(propName, elements.filter(forceUpdate));
            }
            cleanupElements(elmsToUpdate);
        },
        reset: () => {
            elmsToUpdate.forEach((elms) => elms.forEach(forceUpdate));
            cleanupElements(elmsToUpdate);
        },
    };
};

const unwrap = (val) => (typeof val === 'function' ? val() : val);
const createObservableMap = (defaultState, shouldUpdate = (a, b) => a !== b) => {
    const unwrappedState = unwrap(defaultState);
    let states = new Map(Object.entries(unwrappedState ?? {}));
    const handlers = {
        dispose: [],
        get: [],
        set: [],
        reset: [],
    };
    const reset = () => {
        // When resetting the state, the default state may be a function - unwrap it to invoke it.
        // otherwise, the state won't be properly reset
        states = new Map(Object.entries(unwrap(defaultState) ?? {}));
        handlers.reset.forEach((cb) => cb());
    };
    const dispose = () => {
        // Call first dispose as resetting the state would
        // cause less updates ;)
        handlers.dispose.forEach((cb) => cb());
        reset();
    };
    const get = (propName) => {
        handlers.get.forEach((cb) => cb(propName));
        return states.get(propName);
    };
    const set = (propName, value) => {
        const oldValue = states.get(propName);
        if (shouldUpdate(value, oldValue, propName)) {
            states.set(propName, value);
            handlers.set.forEach((cb) => cb(propName, value, oldValue));
        }
    };
    const state = (typeof Proxy === 'undefined'
        ? {}
        : new Proxy(unwrappedState, {
            get(_, propName) {
                return get(propName);
            },
            ownKeys(_) {
                return Array.from(states.keys());
            },
            getOwnPropertyDescriptor() {
                return {
                    enumerable: true,
                    configurable: true,
                };
            },
            has(_, propName) {
                return states.has(propName);
            },
            set(_, propName, value) {
                set(propName, value);
                return true;
            },
        }));
    const on = (eventName, callback) => {
        handlers[eventName].push(callback);
        return () => {
            removeFromArray(handlers[eventName], callback);
        };
    };
    const onChange = (propName, cb) => {
        const unSet = on('set', (key, newValue) => {
            if (key === propName) {
                cb(newValue);
            }
        });
        // We need to unwrap the defaultState because it might be a function.
        // Otherwise we might not be sending the right reset value.
        const unReset = on('reset', () => cb(unwrap(defaultState)[propName]));
        return () => {
            unSet();
            unReset();
        };
    };
    const use = (...subscriptions) => {
        const unsubs = subscriptions.reduce((unsubs, subscription) => {
            if (subscription.set) {
                unsubs.push(on('set', subscription.set));
            }
            if (subscription.get) {
                unsubs.push(on('get', subscription.get));
            }
            if (subscription.reset) {
                unsubs.push(on('reset', subscription.reset));
            }
            if (subscription.dispose) {
                unsubs.push(on('dispose', subscription.dispose));
            }
            return unsubs;
        }, []);
        return () => unsubs.forEach((unsub) => unsub());
    };
    const forceUpdate = (key) => {
        const oldValue = states.get(key);
        handlers.set.forEach((cb) => cb(key, oldValue, oldValue));
    };
    return {
        state,
        get,
        set,
        on,
        onChange,
        use,
        dispose,
        reset,
        forceUpdate,
    };
};
const removeFromArray = (array, item) => {
    const index = array.indexOf(item);
    if (index >= 0) {
        array[index] = array[array.length - 1];
        array.length--;
    }
};

const createStore = (defaultState, shouldUpdate) => {
    const map = createObservableMap(defaultState, shouldUpdate);
    map.use(stencilSubscription());
    return map;
};

const { state: stepState, onChange } = createStore({
    step: 1
});
const StepNavigation = ({ t, multipleSteps }) => multipleSteps ? (h("div", { class: "ux-u-margin-top-40 ux-u-margin-bottom-40" },
    h("ux-steps", null,
        h("ux-steps-item", { onClick: () => (stepState.step = 1), number: 1, current: stepState.step === 1, done: stepState.step > 1 }, t("headline.caseDetails")),
        h("ux-steps-item", { onClick: () => (stepState.step = 2), number: 2, current: stepState.step === 2 }, t("headline.contactData"))))) : null;
const StepNext = ({ t, multipleSteps, onCancel }) => multipleSteps ? (h("div", { class: "ux-row" },
    h("div", { class: "ux-col-12 ux-u-margin-bottom-20" },
        h("div", { class: "ux-text-normal ux-text-normal--light-gray" },
            h("p", null, t("validation.requiredFieldsExplanation")))),
    h("div", { class: "ux-col-12" },
        h("div", { class: "ux-u-d-flex ux-u-flex-jc-between ux-u-flex-dir-row-reverse" },
            h("ux-button", { variant: "primary", onClick: () => (stepState.step = 2) }, t("button.next")),
            h("ux-button", { type: "button", onClick: () => onCancel() }, t("button.cancel")))))) : null;
const StepBack = ({ t, multipleSteps }) => multipleSteps ? (h("ux-button", { type: "button", onClick: () => (stepState.step = 1) }, t("button.back"))) : null;

/**
 * Decodes a JWT token to an object
 * implementation from https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
 * @param token JWT token
 * @returns object
 */
function decodeJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""));
    return JSON.parse(jsonPayload);
}

const getMachineList = async (apiClient) => {
    const idToken = getAuthClient(authClientName).getIdToken();
    const decoded = decodeJwt(idToken);
    const { customerNumber } = decoded;
    return apiClient
        .request(`/asset-management/1.0/core/assets/parameters?customerOrgId=${customerNumber}&isCustomOnly=false&isRootAsset=true`, {
        method: "GET",
        mockResponse
    })
        .then((response) => {
        if (response.assets) {
            return response.assets;
        }
        return Promise.reject("No assets found");
    });
};
const mockResponse = {
    assets: [
        {
            assetId: "A0660A0576",
            assetName: "Quicksharp",
            technologyId: "PUNCHING"
        },
        {
            assetId: "LS00093708",
            assetName: "HL 54P",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "E0551A0120",
            assetName: "HL 101P",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "LS00009160",
            assetName: "HL 2006D",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "LS00093844",
            assetName: "HL 2006D",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "N0053A0020",
            assetName: "SortMaster (AL28)",
            technologyId: "AUTOMATION"
        },
        {
            assetId: "C0200I0005",
            assetName: "Toolshuttle",
            technologyId: "AUTOMATION"
        },
        {
            assetId: "883850",
            assetName: "TRBV85SX",
            technologyId: "BENDING"
        },
        {
            assetId: "B0501A0185",
            assetName: "TruBend 5050",
            technologyId: "BENDING"
        },
        {
            assetId: "B1503I0001",
            assetName: "TruBend 5085 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1504I0151",
            assetName: "TruBend 5130 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1505I0280",
            assetName: "TruBend 5170 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1505I0637",
            assetName: "TruBend 5170 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1507I0001",
            assetName: "TruBend 5230 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1509I0001",
            assetName: "TruBend 5320 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B0702A2028",
            assetName: "TruBend 7036",
            technologyId: "BENDING"
        },
        {
            assetId: "B0702A2031",
            assetName: "TruBend 7036",
            technologyId: "BENDING"
        },
        {
            assetId: "B1703I0086",
            assetName: "TruBend 7050 (B28)",
            technologyId: "BENDING"
        },
        {
            assetId: "E0953A0235",
            assetName: "TruDisk 2001 (4C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E0953A0078",
            assetName: "TruDisk 4002 (4C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E1153A0485",
            assetName: "TruDisk 4002 (4C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E1153A0426",
            assetName: "TruDisk 6602 (4 C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E0953A0227",
            assetName: "TruDisk 8002 (2C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E1053A0162",
            assetName: "TruDisk  3001 (4 C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "A0221E0833",
            assetName: "TruLaser 3030 fiber (L49)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A0221E1065",
            assetName: "TruLaser 3030 fiber (L49)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A2221E0146",
            assetName: "TruLaser 3030 fiber (L95)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A3231E0025",
            assetName: "TruLaser 5030 fiber (L76)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A3231E0518",
            assetName: "TruLaser 5030 fiber (L76)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "S2321M0144",
            assetName: "TruLaser Cell 3000 (L34)",
            technologyId: "WELDING"
        },
        {
            assetId: "S0541C0287",
            assetName: "TruLaser Cell 7040 (L31) fiber",
            technologyId: "3DLASERCUTTING"
        },
        {
            assetId: "S0541C0328",
            assetName: "TruLaser Cell 7040 (L31) fiber",
            technologyId: "3DLASERCUTTING"
        },
        {
            assetId: "A0280E0038",
            assetName: "TruLaser Center 7030 (L26)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A0280E0127",
            assetName: "TruLaser Center 7030 (L26)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A0455A0024",
            assetName: "TruLaser Robot 5020",
            technologyId: "LASERTUBECUTTING"
        },
        {
            assetId: "A0455A0037",
            assetName: "TruLaser Robot 5020",
            technologyId: "LASERTUBECUTTING"
        },
        {
            assetId: "A1441D0079",
            assetName: "TruLaser Tube 7000 fiber",
            technologyId: "LASERTUBECUTTING"
        },
        {
            assetId: "A0150A0160",
            assetName: "TruMatic 7000",
            technologyId: "COMBINED"
        },
        {
            assetId: "A1150D0126",
            assetName: "TruMatic 7000 (K08) Medium format",
            technologyId: "COMBINED"
        },
        {
            assetId: "A0460A0004",
            assetName: "TrumaWeld L 1000",
            technologyId: "WELDING"
        },
        {
            assetId: "A0420A0232",
            assetName: "TRUMPF Lasercell 1005",
            technologyId: "3DLASERCUTTING"
        },
        {
            assetId: "S0711Q0095",
            assetName: "TruPrint 1000 (G01)",
            technologyId: "3DPRINTING"
        },
        {
            assetId: "E0851A0036",
            assetName: "TruPulse 203",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "E0851A0048",
            assetName: "TruPulse 556",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "E0652A0387",
            assetName: "VectorMark Compact 1",
            technologyId: "MARKINGLASER"
        }
    ]
};

const getNotificationTypes = async (apiClient) => {
    return apiClient.request("/servicecall/getnotificationtypes", {
        mockResponse: mockNotificationTypes
    });
};
const NOTIFICATION_TYPES = {
    MACHINE_STOP: 20,
    FAULT_WITHOUT_STOP: 10,
    PROGRAMMING_SOFTWARE: -100,
    CLARIFICATION_SPARE_PARTS: 50,
    OTHER: 40,
    MAINTENANCE_REQUEST: 120,
    SPARE_PARTS_ORDER: 140,
    APPLICATION_ISSUES: 80
};
const mockNotificationTypes = [
    {
        Id: NOTIFICATION_TYPES.MACHINE_STOP,
        LabelKey: "Maschinenstillstand"
    },
    {
        Id: NOTIFICATION_TYPES.FAULT_WITHOUT_STOP,
        LabelKey: "Störung ohne Stillstand"
    },
    {
        Id: NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE,
        LabelKey: "Programmierung / Software"
    },
    {
        Id: NOTIFICATION_TYPES.CLARIFICATION_SPARE_PARTS,
        LabelKey: "Klärung Ersatzteile"
    },
    {
        Id: NOTIFICATION_TYPES.OTHER,
        LabelKey: "Sonstiges"
    },
    {
        Id: NOTIFICATION_TYPES.MAINTENANCE_REQUEST,
        LabelKey: "Wartungsanfrage"
    },
    {
        Id: NOTIFICATION_TYPES.SPARE_PARTS_ORDER,
        LabelKey: "Bestellung Ersatzteile"
    },
    {
        Id: NOTIFICATION_TYPES.APPLICATION_ISSUES,
        LabelKey: "Applikationsthemen"
    }
];

const createApiStore = () => {
    const store = createStore({
        machinesLoading: true,
        machines: [],
        softwaresLoading: true,
        softwares: [],
        softwareTypes: [],
        notificationTypesLoading: true,
        notificationTypes: [],
        loading: true
    });
    store.onChange("machinesLoading", () => updateLoading());
    store.onChange("softwaresLoading", () => updateLoading());
    store.onChange("notificationTypesLoading", () => updateLoading());
    function updateLoading() {
        store.state.loading = store.state.machinesLoading || store.state.softwaresLoading || store.state.notificationTypesLoading;
    }
    return store;
};
const loadFormDataFromPublicApi = async (apiStore, apiClient) => {
    apiStore.state.softwaresLoading = true;
    apiStore.state.notificationTypesLoading = true;
    getSoftwares(apiClient).then((softwares) => {
        apiStore.state.softwares = softwares;
        apiStore.state.softwareTypes = getSoftwareTypes(softwares);
        apiStore.state.softwaresLoading = false;
    });
    getNotificationTypes(apiClient).then((notificationTypes) => {
        apiStore.state.notificationTypes = notificationTypes;
        apiStore.state.notificationTypesLoading = false;
    });
};
const loadFormDataFromAssetManagementApi = async (apiStore, apiClient) => {
    apiStore.state.machinesLoading = true;
    getMachineList(apiClient).then((machines) => {
        apiStore.state.machines = machines;
        apiStore.state.machinesLoading = false;
    });
};

const authClientServiceCalls = getAuthClient(authClientName);
const createAuthStore = () => {
    const store = createStore({
        isAuthenticating: false,
        authError: null
    });
    return store;
};
const SilentAuth = ({ disabled, config, authStore }) => {
    if (disabled || config.useMockData)
        return;
    const onAuthError = (e) => {
        authStore.state.authError = e.detail.error;
        if (authStore.state.authError !== null) {
            authStore.state.isAuthenticating = false;
        }
    };
    return (h("ux-u-silent-auth", { "client-name": authClientName, "client-id": config.authClientId, issuer: config.authIssuer, scope: config === null || config === void 0 ? void 0 : config.authScope, onUxAuth: (e) => onAuthError(e) }));
};
/**
 * utility to execute a function as soon as the user is authenticated
 * @param fn callback to be executed as soon as the user is authenticated
 * @returns a function to unsubscribe
 */
function whenAuthenticated(callback) {
    const { useMockData } = getEnvConfig();
    if (authClientServiceCalls.getAccessToken() !== null || useMockData) {
        callback();
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => { };
    }
    return authClientServiceCalls.subscribe(callback);
}

const APPLICATION_IDS = {
    WELDING: 1,
    PLASTIC: 2,
    CUTTING: 3,
    HARDENING: 4,
    MARKING: 5,
    OTHER: 6,
    FIXED_OPTICS_BEO: 7,
    PFO20_33: 8,
    PFO3D: 9,
    I_PFO: 10,
    OTHER_OPTICS: 11,
    WELD_DEPTH: 12,
    HARDNESS: 13,
    CRACKING: 14,
    BUBBLE_SPLATTER: 15,
    PROCESS_DATA: 16,
    OTHER_WELDING: 17
};
const applicationsLaser = [
    {
        labelKey: "welding",
        id: APPLICATION_IDS.WELDING
    },
    {
        labelKey: "plastic",
        id: APPLICATION_IDS.PLASTIC
    },
    {
        labelKey: "cutting",
        id: APPLICATION_IDS.CUTTING
    },
    {
        labelKey: "hardening",
        id: APPLICATION_IDS.HARDENING
    },
    {
        labelKey: "marking",
        id: APPLICATION_IDS.MARKING
    },
    {
        labelKey: "other",
        id: APPLICATION_IDS.OTHER
    }
];
const applicationsOptics = [
    {
        labelKey: "fixedOpticsBEO",
        id: APPLICATION_IDS.FIXED_OPTICS_BEO
    },
    {
        labelKey: "PFO20/33",
        id: APPLICATION_IDS.PFO20_33
    },
    {
        labelKey: "PFO3D",
        id: APPLICATION_IDS.PFO3D
    },
    {
        labelKey: "I-PFO",
        id: APPLICATION_IDS.I_PFO
    },
    {
        labelKey: "other",
        id: APPLICATION_IDS.OTHER_OPTICS
    }
];
const applicationsWelding = [
    {
        labelKey: "weldDepth",
        id: APPLICATION_IDS.WELD_DEPTH
    },
    {
        labelKey: "hardness",
        id: APPLICATION_IDS.HARDNESS
    },
    {
        labelKey: "cracking",
        id: APPLICATION_IDS.CRACKING
    },
    {
        labelKey: "bubbleSplatter",
        id: APPLICATION_IDS.BUBBLE_SPLATTER
    },
    {
        labelKey: "processData",
        id: APPLICATION_IDS.PROCESS_DATA
    },
    {
        labelKey: "other",
        id: APPLICATION_IDS.OTHER_WELDING
    }
];

const hideMachinesForNotificationTypes = [NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE];
const showErrorNumbersForNotificationTypes = [
    null,
    NOTIFICATION_TYPES.APPLICATION_ISSUES,
    NOTIFICATION_TYPES.MACHINE_STOP,
    NOTIFICATION_TYPES.FAULT_WITHOUT_STOP
];
const hideErrorNumbersForTechnologies = ["ELECTRONICS"];
const showSoftwareFieldsForNotificationTypes = [NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE];
const showSoftwareModulesForSoftwareTypes = [SOFTWARE_TYPES.TRUTOPS, SOFTWARE_TYPES.TRUTOPS_FAB, SOFTWARE_TYPES.TRUTOPS_BOOST];
const showApplicationIssuesForNotificationTypes = [NOTIFICATION_TYPES.APPLICATION_ISSUES];
const showApplicationIssuesForTechnologies = [
    "PULSEDLASER",
    "SHORTPULSEDLASER",
    "DISKLASER",
    "FIBERLASER",
    "DIODELASER",
    "MARKINGLASER"
];
const showWeldingApplicationFieldsForApplication = [APPLICATION_IDS.WELDING];
const createFormStore = () => {
    const store = createStore({
        selectedNotificationType: null,
        selectedSoftwareType: null,
        selectedApplication: null,
        formIsSubmitted: false,
        selectedMachineTechnology: null,
        showErrorNumbers: true,
        showApplications: false,
        showSoftwareModules: false,
        showMachines: true,
        showSoftwareFields: false,
        showWeldingApplicationFields: false
    });
    store.onChange("selectedNotificationType", (selectedNotificationType) => {
        store.state.showMachines = !hideMachinesForNotificationTypes.includes(selectedNotificationType);
        store.state.showSoftwareFields = showSoftwareFieldsForNotificationTypes.includes(selectedNotificationType);
        determineShowErrorNumbers();
        determineShowApplications();
    });
    store.onChange("selectedMachineTechnology", () => {
        determineShowErrorNumbers();
        determineShowApplications();
    });
    store.onChange("selectedApplication", (selectedApplication) => {
        store.state.showWeldingApplicationFields = showWeldingApplicationFieldsForApplication.includes(selectedApplication);
    });
    store.onChange("selectedSoftwareType", (selectedSoftwareType) => {
        store.state.showSoftwareModules = showSoftwareModulesForSoftwareTypes.includes(selectedSoftwareType);
    });
    function determineShowApplications() {
        const { selectedNotificationType, selectedMachineTechnology } = store.state;
        store.state.showApplications =
            showApplicationIssuesForNotificationTypes.includes(selectedNotificationType) &&
                showApplicationIssuesForTechnologies.includes(selectedMachineTechnology);
    }
    function determineShowErrorNumbers() {
        const { selectedNotificationType, selectedMachineTechnology } = store.state;
        store.state.showErrorNumbers =
            showErrorNumbersForNotificationTypes.includes(selectedNotificationType) &&
                !hideErrorNumbersForTechnologies.includes(selectedMachineTechnology);
    }
    return store;
};

const ErrorNumberInput = ({ t, prefillData }) => {
    var _a, _b, _c, _d;
    return (h("div", { class: "ux-u-margin-bottom-20" },
        h("label", { class: "ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase" }, t("field.errorNumber.label")),
        h("service-calls-dynamic-input", { "add-text": t("field.errorNumber.addAnotherNumber"), "input-name": "errorNumber1", prefilledValues: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _a === void 0 ? void 0 : _a.errorNumbers) || [], "init-items": ((_c = (_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _b === void 0 ? void 0 : _b.errorNumbers) === null || _c === void 0 ? void 0 : _c.length) || 1, "min-items": "1", "max-items": "6", disabled: !!((_d = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _d === void 0 ? void 0 : _d.errorNumbers) },
            h("div", { class: "mf-create-service-case__help-block", slot: "info" },
                h("p", { class: "ux-text-tiny" }, t("field.errorNumber.explanation", {
                    example1: "0000FC00",
                    example2: "1.10.58102409",
                    example3: "6000"
                }))))));
};

const SALUTATION = {
    FEMALE: 1,
    MALE: 2
};

const getLanguageFromLocale = (locale) => {
    if (!locale.includes("-")) {
        return locale;
    }
    return locale.split("-")[0];
};
const getCountryFromLocale = (locale) => {
    if (!locale.includes("-")) {
        return null;
    }
    return locale.split("-")[1];
};

const SalutationInput = ({ t, prefillData }) => {
    var _a, _b, _c;
    return (h("ux-select", { label: t("field.salutation.label"), required: true, name: "salutation" },
        h("option", { value: "", selected: typeof ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.salutation) !== "number", disabled: true }, t("select.placeholder")),
        h("option", { value: SALUTATION.FEMALE, selected: ((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _b === void 0 ? void 0 : _b.salutation) === SALUTATION.FEMALE }, t("field.salutation.female")),
        h("option", { value: SALUTATION.MALE, selected: ((_c = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _c === void 0 ? void 0 : _c.salutation) === SALUTATION.MALE }, t("field.salutation.male"))));
};
const FirstNameInput = ({ t, prefillData }) => {
    var _a;
    return (h("ux-input-field", { "data-validation-required--msg": t("validation.requiredField"), "data-validation-length--max": "45", "data-validation-length--msg": t("validation.maxAllowedCharacters", {
            number: 45
        }), label: t("field.firstName.label"), name: "firstName", value: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.firstName) || "", required: true }));
};
const LastNameInput = ({ t, prefillData }) => {
    var _a;
    return (h("ux-input-field", { "data-validation-required--msg": t("validation.requiredField"), "data-validation-length--max": "45", "data-validation-length--msg": t("validation.maxAllowedCharacters", {
            number: 45
        }), label: t("field.lastName.label"), name: "lastName", value: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.lastName) || "", required: true }));
};
const PhoneInput = ({ t, prefillData, locale }) => {
    var _a;
    return (h("ux-phone-input", { label: t("field.phone.label"), language: getLanguageFromLocale(locale), country: getCountryFromLocale(locale), "data-validation-invalid--msg": t("validation.invalidPhoneNumber"), "data-validation-required--msg": t("validation.requiredField"), name: "phone", value: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.phone) || "", required: true }));
};
const EmailInput = ({ t, prefillData }) => {
    var _a;
    return (h("ux-input-field", { type: "email", required: true, "data-validation-required--msg": t("validation.requiredField"), "data-validation-length--max": "100", "data-validation-length--msg": t("validation.maxAllowedCharacters", {
            number: 100
        }), "data-validation-invalid--msg": t("validation.invalidEmail"), label: "E-Mail", name: "email", value: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.email) || "" }));
};

const classifications = ["standstill", "fault", "slightImpairment", "generalInquiry"];

const SoftwareModuleSelect = ({ t, prefillData, formStore, apiStore }) => {
    var _a, _b;
    return (h("ux-select", { required: true, label: t("field.softwareModule.label"), "data-validation-required--msg": t("validation.requiredField"), name: "softwareModule", disabled: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareModule },
        h("option", { value: "", selected: !((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _b === void 0 ? void 0 : _b.softwareModule), disabled: true }, t("select.placeholder")),
        getSoftwareModulesByType(apiStore.state.softwares, formStore.state.selectedSoftwareType).map((softwareModule) => {
            var _a;
            return (h("option", { value: softwareModule, selected: softwareModule === ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareModule) }, t(`softwareModule.${softwareModule}`)));
        })));
};
const SoftwareTypeSelect = ({ t, prefillData, formStore, apiStore }) => {
    var _a, _b;
    return (h("ux-select", { required: true, label: t("field.softwareType.label"), name: "softwareType", onUxChange: (event) => {
            formStore.state.selectedSoftwareType = event.detail.value;
        }, disabled: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareType },
        h("option", { value: "", selected: !((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _b === void 0 ? void 0 : _b.softwareType), disabled: true }, t("select.placeholder")),
        apiStore.state.softwareTypes.map((softwareType) => {
            var _a;
            return (h("option", { value: softwareType, selected: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareType) === softwareType }, softwareType));
        })));
};
const SoftwareClassificationSelect = ({ t, prefillData }) => {
    var _a;
    const hasPrefilledClassification = classifications.includes((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareProblemClassification);
    return (h("ux-select", { required: true, label: t("field.softwareProblemClassification.label"), "data-validation-required--msg": t("validation.requiredField"), name: "softwareProblemClassification" },
        h("option", { value: "", selected: !hasPrefilledClassification, disabled: true }, t("select.placeholder")),
        classifications.map((classification) => {
            var _a;
            return (h("option", { value: classification, selected: classification === ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareProblemClassification) }, t(`softwareProblemClassification.${classification}`)));
        })));
};
const InstallationCheckbox = ({ t, prefillData }) => {
    var _a, _b;
    return (h("ux-checkbox", { label: t("field.softwareInstallationProblem.label"), name: "installationProblem", checked: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.isInstallationProblem, disabled: typeof ((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _b === void 0 ? void 0 : _b.isInstallationProblem) === "boolean" }));
};
const LicenseCheckbox = ({ t, prefillData }) => {
    var _a, _b;
    return (h("ux-checkbox", { label: t("field.softwareLicenseIssue.label"), name: "licenseIssue", checked: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.isLicenseIssue, disabled: typeof ((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _b === void 0 ? void 0 : _b.isLicenseIssue) === "boolean" }));
};

const ApplicationSelect = ({ t, formStore }) => (h("ux-select", { required: true, label: t("field.application.label"), "data-validation-required--msg": t("validation.requiredField"), name: "application", onUxChange: (event) => {
        formStore.state.selectedApplication = Number(event.detail.value);
    } },
    h("option", { value: "", selected: true, disabled: true }, t("select.placeholder")),
    applicationsLaser.map((application) => (h("option", { value: application.id }, t(`application.${application.labelKey}`))))));
const ApplicationOpticsSelect = ({ t }) => (h("ux-select", { label: t("field.optics.label"), name: "optics" },
    h("option", { value: "", selected: true, disabled: true }, t("select.placeholder")),
    applicationsOptics.map((application) => (h("option", { value: application.id }, t(`application.${application.labelKey}`))))));
const ApplicationIssueSelect = ({ t }) => (h("ux-select", { label: t("field.applicationIssue.label"), name: "applicationIssue" },
    h("option", { value: "", selected: true, disabled: true }, t("select.placeholder")),
    applicationsWelding.map((application) => (h("option", { value: application.id }, t(`application.${application.labelKey}`))))));

const MachineSelect = ({ t, prefillData, apiStore, formStore }) => {
    var _a, _b;
    const { machines } = apiStore.state;
    return (h("ux-select", { required: true, label: t("field.machine.label"), "data-validation-required--msg": t("validation.requiredField"), name: "productNo", onUxChange: (event) => {
            const machine = machines.find(({ assetId }) => assetId === event.detail.value);
            if (machine) {
                formStore.state.selectedMachineTechnology = machine.technologyId;
                return;
            }
            // eslint-disable-next-line no-console
            console.error(`Machine with id ${event.detail.value} not found`);
        }, disabled: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _a === void 0 ? void 0 : _a.assetId },
        h("option", { value: "", selected: !((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _b === void 0 ? void 0 : _b.assetId), disabled: true }, t("select.placeholder")),
        machines.map((machine) => {
            var _a;
            return (h("option", { key: machine.assetId, value: machine.assetId, selected: machine.assetId === ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _a === void 0 ? void 0 : _a.assetId) },
                machine.assetName,
                " (",
                machine.assetId,
                ")"));
        })));
};

const NotificationTypeSelect = ({ t, prefillData, apiStore, formStore }) => {
    const { notificationTypes } = apiStore.state;
    const { selectedNotificationType } = formStore.state;
    return (h("ux-select", { required: true, label: t("field.reason.label"), "data-validation-required--msg": t("validation.requiredField"), name: "notificationType", onUxChange: (event) => {
            formStore.state.selectedNotificationType = Number(event.detail.value);
        }, disabled: prefillData === null || prefillData === void 0 ? void 0 : prefillData.notificationType },
        h("option", { value: "", selected: !(prefillData === null || prefillData === void 0 ? void 0 : prefillData.notificationType), disabled: true }, t("select.placeholder")),
        notificationTypes.map((notificationType) => (h("option", { key: String(notificationType.LabelKey), value: notificationType.Id, selected: notificationType.Id === selectedNotificationType }, t(`notificationType.${notificationType.LabelKey}`))))));
};

const assetManagementApiClients = {};
const getAssetManagementApiClient = (config) => {
    const { assetManagementApiBaseUrl, authIssuer, authScope, authClientId } = config;
    const key = `${assetManagementApiBaseUrl}-${authIssuer}-${authScope}-${authClientId}`;
    if (!assetManagementApiClients[key]) {
        assetManagementApiClients[key] = new AssetManagementApiClient(config);
    }
    return assetManagementApiClients[key];
};
class AssetManagementApiClient extends ApiClient {
    /**
     * Request function to send an authenticated POST request to the AssetManagementAPI
     */
    async request(path, options) {
        if (this.config.useMockData) {
            return this.mockRequest(options === null || options === void 0 ? void 0 : options.mockResponse);
        }
        if (this.authClientServiceCalls === null) {
            return Promise.reject("AuthClient not initialized");
        }
        const { method = "POST", headers: additionalHeaders = {}, body: bodyObject } = options || {};
        const headers = Object.assign({ Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${this.token}` }, additionalHeaders);
        const body = bodyObject ? JSON.stringify(bodyObject) : undefined;
        return fetch(`${this.config.assetManagementApiBaseUrl}${path}`, {
            method,
            headers,
            body
        }).then((response) => {
            if (response.status === 401) {
                this.authClientServiceCalls.deleteAccessToken();
                return Promise.reject("Unauthorized");
            }
            if (response.status === 200) {
                return response.json();
            }
            return Promise.reject(`Error ${response.status}`);
        });
    }
}

const createServiceCaseCss = ".mf-create-service-case{display:block}.mf-create-service-case__button-group{display:flex;gap:0 10px;}mf-create-service-case ux-select{width:100%}";
const MfCreateServiceCaseStyle0 = createServiceCaseCss;

const MfCreateServiceCase = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.serviceCaseSubmit = createEvent(this, "mfServiceCaseSubmit", 7);
        this.serviceCaseCancel = createEvent(this, "mfServiceCaseCancel", 7);
        this.apiStore = createApiStore();
        this.authStore = createAuthStore();
        this.formStore = createFormStore();
        this.callId = crypto.randomUUID(); // this browser API only works on localhost and over https
        this.multipleSteps = false;
        this.fileUpload = false;
        this.prefillData = null;
        this.locale = "en-US";
        this.env = "prod";
        this.legacyApi = false;
        this.disableSilentAuth = false;
        this.tbdbError = null;
    }
    connectedCallback() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.publicApiClient = getPublicApiClient(this.config);
        this.unsubscribeToTbdbToken = this.publicApiClient.subscribeToTbdbToken((event) => {
            if (event.success) {
                loadFormDataFromPublicApi(this.apiStore, this.publicApiClient);
            }
            else {
                this.tbdbError = event.errorMessage || "Error getting TBDB token";
            }
        });
        this.assetManagementApiClient = getAssetManagementApiClient(this.config);
        this.loadTranslations();
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.unsubscribeAuth = whenAuthenticated(() => loadFormDataFromAssetManagementApi(this.apiStore, this.assetManagementApiClient));
        this.apiStore.onChange("notificationTypesLoading", (notificationTypesLoading) => {
            var _a;
            if (!notificationTypesLoading) {
                this.formStore.state.selectedNotificationType = ((_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.notificationType) || null;
            }
        });
    }
    disconnectedCallback() {
        this.unsubscribeAuth();
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        if (this.unsubscribeToTbdbToken) {
            this.unsubscribeToTbdbToken();
        }
    }
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }
    onPrefillDataChange() {
        var _a;
        const notificationType = (_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.notificationType;
        if (notificationType) {
            this.formStore.state.selectedNotificationType = notificationType;
        }
    }
    loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }
    onSubmit(event) {
        event.preventDefault();
        const { softwares } = this.apiStore.state;
        const { form, callId } = this;
        const data = createRequestData({
            form,
            callId,
            softwares,
            selectedMachineTechnology: this.formStore.state.selectedMachineTechnology
        });
        this.serviceCaseSubmit.emit(data);
        this.formStore.state.formIsSubmitted = true;
        if (this.config.logging) {
            // eslint-disable-next-line no-console
            console.log("Form submitted", data);
        }
    }
    onCancel() {
        this.serviceCaseCancel.emit();
    }
    render() {
        const t = (key, options = undefined) => i18nProvider.t(`mf-service-calls:${String(key)}`, options);
        const { loading } = this.apiStore.state;
        const { isAuthenticating, authError } = this.authStore.state;
        const { prefillData, multipleSteps } = this;
        const { showErrorNumbers, formIsSubmitted, showApplications, showSoftwareModules, showMachines, showSoftwareFields, showWeldingApplicationFields } = this.formStore.state;
        const showStep1 = ((multipleSteps && stepState.step === 1) || !multipleSteps) && !(loading || isAuthenticating)
            ? {}
            : { display: "none" };
        const showStep2 = ((multipleSteps && stepState.step === 2) || !multipleSteps) && !(loading || isAuthenticating)
            ? {}
            : { display: "none" };
        return (h(Host, { key: '67ed03f8185a19634e057225a4f830f2ab9da61d' }, !formIsSubmitted && (h("ux-form", { key: '9f3022c3feba3dcade26a9a2d40e2952090cdeb7', "data-validation-required--msg": t("validation.requiredField") }, h("form", { key: 'bfef6b1d209c660a09e02bfb1e3bb1c868257a3b', onSubmit: (e) => this.onSubmit(e), ref: (el) => (this.form = el) }, h("div", { key: '1598e5bde996ad8f264fb2de505699c9889cdd4b', class: "ux-u-text-align--center ux-u-margin-top-60 ux-u-margin-bottom-60" }, h("div", { key: '2da93ee0c508928f8fb335f197f758a13cde2bff', class: "ux-text-headline ux-text-headline--giant" }, t("headline.createCase"))), h(StepNavigation, { key: '0195d0f8a52fea698026d26595e2013cfaf97ed9', t: t, multipleSteps: multipleSteps }), (loading || isAuthenticating) && h("ux-loader", { key: '959e7e0cbac9748086d3d15b89d69eee4a4f5f51', variant: "spinner" }), authError && (h("div", { key: '04550c254735256c48b74481b723764c347fa4a7', class: "ux-u-text-align--center ux-text-normal ux-u-margin-top-20 ux-u-margin-bottom-20" }, t("auth.error"))), h("div", { key: '3ddf1852017c99dbd10e281650b8d9ad10a0cd84', class: "ux-row ux-u-flex-jc-center", style: showStep1 }, h("div", { key: 'cd7795c9ae8103a3aa2bfffa01d32171dd499f7e', class: "ux-col-12 ux-col-md-10" }, h("div", { key: '3b2c5bddf6cec272ff28e3e8a3463acbcbd448df', class: "ux-row" }, h("div", { key: '9e0410274235ac921a698a24fc9e13e1c2826b3e', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'e6c5b33c66f3b76027fda1c17d09186a92ccabbd', class: "ux-u-margin-bottom-20" }, h(NotificationTypeSelect, { key: '0ce6ef4c1a525bdb1b8c85b8236a3a674f991adb', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))), showMachines && (h("div", { key: 'b4923dea09479bdf5d073f6376a81b8b80a7d484', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '0de3870f3a1d256f97e5e65376d47ebe9fa4c99b', class: "ux-u-margin-bottom-20" }, h(MachineSelect, { key: 'a7a996ed2d80b14dd02f6b498d29a4f70bfbb225', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))))), showApplications && (h(Fragment, { key: '7c0d60b3bc48d51be371a2322e8b49c58439cb28' }, h("div", { key: '647ad43c73d5d2e65f96e15d5bff88dac5666e9a', class: "ux-row" }, h("div", { key: 'aec3e08d3ba5f6e1fbcc164f68bd8f1bb61bc15f', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '3bc26cc2843e82f503ce34172323daecbdc6263d', class: "ux-u-margin-bottom-20" }, h(ApplicationSelect, { key: '0581dbb28d931c2c1a300ae4e50bd3bb2875a6e6', t: t, formStore: this.formStore })))), showWeldingApplicationFields && (h("div", { key: '52425d30da2f622c18e544db59f30740395971e1', class: "ux-row" }, h("div", { key: 'f4d87cc21ebc34272adf32de7cc84e0bc0e49a11', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'e1fff49cb26af42059b23a7ab99224e7bc8dda20', class: "ux-u-margin-bottom-20" }, h(ApplicationOpticsSelect, { key: '361d1f0cbc979d8de4e77b15d96143e220e27d63', t: t }))), h("div", { key: '180e204180760e8b66a13cd4226fd74e0cbb69c1', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '9e0ee2516d4deeae10b690288b28e8b4fa9a97a6', class: "ux-u-margin-bottom-20" }, h(ApplicationIssueSelect, { key: 'e41e6385c88afa9049dd57ffbd0e6a1e4297875e', t: t }))))))), showSoftwareFields && (h(Fragment, { key: 'e9a947ece0e7881cd75c4c523843636cc0808e9d' }, h("div", { key: '7cdd41347726f8bff642bb38dd1f729b59720fb9', class: "ux-row" }, h("div", { key: '2a79c4bb76554cb7832a2d58fb9473312d83b46b', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'b19b516d3669a808bdad3adb5fd8ae1596943bca', class: "ux-u-margin-bottom-20" }, h(SoftwareClassificationSelect, { key: 'd15ae097914495b616bcb1456f08e9f5a9ec8fc9', t: t, prefillData: prefillData })))), h("div", { key: '287ea49c7f3b2e958828d396d58f1ffa535f39ed', class: "ux-row" }, h("div", { key: '09cb43c49a2dc04d11b61e26a57adefdae6bb26b', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '33255541c12dbe60d862a8cc36a68f6bc9a97f8a', class: "ux-u-margin-bottom-20" }, h(InstallationCheckbox, { key: '8c4e5199a719bdd6194a56515b9f29c7391e4ea4', t: t, prefillData: prefillData }))), h("div", { key: 'a0c3d3d5914ef827c1be10d41c9db565efd8d7a3', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'ddf667d699de4500fb48574296cc63bca0fcb74e', class: "ux-u-margin-bottom-20" }, h(LicenseCheckbox, { key: 'f3e92eb50839068739daa2fd40ac8118857df614', t: t, prefillData: prefillData })))), h("div", { key: '5d64a347d7bb3b2378589238caa5ffff4dd3d6be', class: "ux-row" }, h("div", { key: 'e5c49cb61dd83616f364fcb6b1f100dc28598312', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '83e71fa83c4e72cfc259f24fff7966eb634fb629', class: "ux-u-margin-bottom-20" }, h(SoftwareTypeSelect, { key: '6df384eafc3d4f1fb54434e530e47287a493afe4', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))), showSoftwareModules && (h("div", { key: '1b9261899912f72f0be68efa90a733a37d27355a', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'f1a953576f892a88806fa39ececab6ff7a678416', class: "ux-u-margin-bottom-20" })))), h("div", { key: 'df0bcf4ac4c4a3c8a6701a97e724610a885eee97', class: "ux-row" }, showSoftwareModules && (h("div", { key: '2551f67541a6e7f5956c9d97d4500ff32da15dcc', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '00280ee20b1dc762c459217ad419c64f1d06e9a7', class: "ux-u-margin-bottom-20" }, h(SoftwareModuleSelect, { key: '4876415350ad763134b5b55acf8c20c3d83e00da', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))))))), showErrorNumbers && (h("div", { key: 'fe7cf1c383c9e3c7c9795a618bbc8ece2ef6cb5c', class: "ux-row" }, h("div", { key: '49c818b2b7ce0c12891d9fe8f2c99a4d180654ae', class: "ux-col-12" }, h(ErrorNumberInput, { key: 'd7e112682fc3fea67d1ad2d4f951ff6d29e8cacf', t: t, prefillData: prefillData })))), h("div", { key: '1a2ace1344e632d3fdcef598e1ba38af565a0d95', class: "ux-row" }, h("div", { key: '263ba8319b402c4ddd7950a3ef22b5350cb6fb23', class: "ux-col-12" }, h("div", { key: '1aac6d3107348d87dcd3cb4b4905f24399213ed4', class: "ux-u-margin-bottom-20" }, h("ux-textarea-field", { key: '3f773daf30a58684e28fceeccfe48459aafab97f', label: t("field.message.label"), required: true, "data-validation-required--msg": t("validation.requiredField"), name: "description" })))), this.fileUpload && (h("div", { key: '65f500a0dfd4521a66345458a1f7fd98824de651', class: "ux-row" }, h("div", { key: 'e7881b2603013885b366479e34eaf5ad4c03420e', class: "ux-col-12" }, h("ux-file-upload", { key: 'c1c341736fe4f74d2bdd72232629c78750a2beb9', "max-file-size": "2", label: t("field.fileUpload.label"), "accepted-files": "image/*,application/pdf,text/html", "max-files": "4" })))), h(StepNext, { key: '1d1cc0e381c37deeebd985be8bd4fed557f7b834', t: t, multipleSteps: multipleSteps, onCancel: () => this.onCancel() }))), h("div", { key: '8f36e0abd99c9a4e7e1318ca984b05daaf2e831c', class: "ux-row ux-u-flex-jc-center", style: showStep2 }, h("div", { key: '86f85e4e2329ef80b4fc271234d32d0fe1c3ca15', class: "fields-wrapper ux-col-12 ux-col-md-10" }, h("div", { key: 'df719d5fdf4db648c4b51f3d4ca0a579518147a2' }, h("slot", { key: '954110e448484c47baa7f79385a157bd48c654bc', name: "contacts-button" })), h("div", { key: 'd92f8c89ddbc2c2a93fa713b53d36a62076325ee', class: "ux-row" }, h("div", { key: '85c984d1602ba0185efb7f749e63e21e58f917bc', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'af60941e78c16e40df634cef424b294407dd6494', class: "ux-u-margin-bottom-20" }, h(SalutationInput, { key: '9248583478dff48f3fc084b8805557374d4bf763', t: t, prefillData: prefillData })))), h("div", { key: 'efa7dd0cdccad40afa0862f6d7a9482c10f59cb1', class: "ux-row" }, h("div", { key: 'dd5c25900f9aa56becb2d79108ef290aa7ffee18', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'd15395ac47b19dd5a834de2940ba2ecb0d1d306d', class: "ux-u-margin-bottom-20" }, h(FirstNameInput, { key: '8ca2c9be522265801506d9c215acc54a575e2359', t: t, prefillData: prefillData }))), h("div", { key: '0fedf94de67b2f26e27e3cb61b67ef014b2e879d', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'edc27408728586d0c1204c5236aaf7d82c1626be', class: "ux-u-margin-bottom-20" }, h(LastNameInput, { key: 'ee9cbd97b6454790cf6cf9150e89b898f63b7bc6', t: t, prefillData: prefillData })))), h("div", { key: 'e3cd753d89f028f89788b2e79aa1588d1dde57f9', class: "ux-row" }, h("div", { key: '1876fd5dfe6cfda4545a3ed49d98c3aa13eea7cf', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'fa1634b88e71ce99a51a1379f9fea865f6bb042b', class: "ux-u-margin-bottom-20" }, h(PhoneInput, { key: '5d4fd46aeeb68778643b6680d9ee3ddf3eb72aaf', t: t, prefillData: prefillData, locale: this.locale }))), h("div", { key: '8af2eab197df5126fc288e392f41db86d2016105', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'ae26113ee3350221e8994504ae1ccfc56b31f681', class: "ux-u-margin-bottom-20" }, h(EmailInput, { key: '693a94b28dc43de3ab9c14190e80b02084ed3eca', t: t, prefillData: prefillData })))), h("div", { key: '07e2b6bef55008ca23efeeb57ec3a8413d0000e5', class: "ux-u-margin-bottom-20" }, h("div", { key: '84cf25939674cf2cf3d0406f86d75a850b5ebb43', class: "ux-text-normal ux-text-normal--light-gray" }, h("p", { key: 'a7cf285b0490f63df94fc7f4802ba917a1131e50' }, t("validation.requiredFieldsExplanation")))), h("div", { key: 'f6261f673d499d24c31a1c3fde29e8aea2c2ed45', class: "ux-u-d-flex ux-u-flex-jc-between ux-u-flex-dir-row-reverse" }, h("ux-button", { key: '73e1f17560b4aea65b6da57beee4a693913d7b55', type: "submit", variant: "primary" }, t("button.submit")), h(StepBack, { key: '567f748ede9397351eb377a02f3c5754b8eece5e', t: t, multipleSteps: multipleSteps }), !multipleSteps && (h("ux-button", { key: '0eb697d4d20403535ded937034ecea2f0d8c2010', type: "button", onClick: () => this.onCancel(), formnovalidate: true }, t("button.cancel"))))))))), h("div", { key: 'a6802cccca6570c1d57ec9f3ebffc332c4f676fb', style: { display: formIsSubmitted ? "block" : "none" } }, h("slot", { key: '17eb98b87e151f0874ed1743de1304ee4a59f12d', name: "connector" })), h(SilentAuth, { key: 'd633688719e06e47dbb906bf105f5da560e11533', config: this.config, disabled: this.disableSilentAuth, authStore: this.authStore })));
    }
    static get watchers() { return {
        "locale": ["onLocaleChange"],
        "prefillData": ["onPrefillDataChange"]
    }; }
};
MfCreateServiceCase.style = MfCreateServiceCaseStyle0;

export { MfCreateServiceCase as mf_create_service_case };

//# sourceMappingURL=mf-create-service-case.entry.js.map